import React, { useEffect, useState } from 'react'
import { navigate } from '@reach/router'
import Input from '../components/Input';
import { clientDefinition } from './clientDefinition';
import { getDataInput, validateData, getValues, mapPropsToState } from '../helpers'
import Loading from '../components/Loading';
import ErrorService from '../components/ErrorService';
import Toast from '../components/Toast';
import { find, propEq } from 'ramda'

const url = `${process.env.REACT_APP_API_URL}/widget/`;
const context = process.env.PUBLIC_URL

class ApiError extends Error {
    status: number;
    data: any;
    
    constructor(status: number, data: any, message?: string) {
        super(message || 'API Error');
        this.status = status;
        this.data = data;
        this.name = 'ApiError';
    }
}

export default function Client(props: any) {
    const [clientData, setClientData] = useState(clientDefinition)
    const [showErrors, setShowErrors] = useState(false)
    const [useCfdi, setUseCfdi] = useState([])
    const [states, setStates] = useState([])
    const [countries, setCountries] = useState([])
    const [taxRegime, setTaxRegime] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>('')
    const [cif, setCif] = useState<File | undefined>(undefined)
    const [cifIsLoaded, setCifIsLoaded] = useState(false)
    const [isUploadingCif, setIsUploadingCif] = useState(false)
    const [toastMessage, setToastMessage] = useState<string>('')
    const [showToast, setShowToast] = useState(false)
    const [toastType, setToastType] = useState<'error' | 'success' | 'info' | 'warning'>('error')

    const onChangeField = ({ target }: any) => {
        const data = getDataInput(target, clientData)

        setClientData(data)
        setShowErrors(false)
    }

    useEffect(() => {
        let requests = 4;
        const validateRequest = () => {
            requests = requests - 1;
            if (requests === 0) {
                setIsLoading(false)
            }
        }
        fetch(`${url}catalogs/use-cfdi`, {
            method: 'POST',
            body: `{"token": "${props.token}"}`,
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(async response => {
            const data = await response.json();
            if (!response.ok) {
                throw new ApiError(response.status, data);
            }
            return data;
        })
            .then(({ data }) => {
                setUseCfdi(data)
                validateRequest()
            })
        .catch(error => {
            console.error('Error al obtener los catálogos:', error);
            setErrorMessage(error.data?.message || 'Error al obtener los catálogos');
            setHasError(true)
            setIsLoading(false)
        });
        fetch(`${url}catalogs/states`, {
            method: 'POST',
            body: `{"token": "${props.token}"}`,
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(async response => {
            const data = await response.json();
            if (!response.ok) {
                throw new ApiError(response.status, data);
            }
            return data;
        })
            .then(({ data }) => {
                setStates(data)
                validateRequest()
            })
        .catch(error => {
            console.error('Error al obtener los catálogos:', error);
            setErrorMessage(error.data?.message || 'Error al obtener los catálogos');
            setHasError(true)
            setIsLoading(false)
        });
        fetch(`${url}catalogs/countries`, {
            method: 'POST',
            body: `{"token": "${props.token}"}`,
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(async response => {
            const data = await response.json();
            if (!response.ok) {
                throw new ApiError(response.status, data);
            }
            return data;
        })
            .then(({ data }) => {
                setCountries(data)
                validateRequest()
            })
        .catch(error => {
            console.error('Error al obtener los catálogos:', error);
            setErrorMessage(error.data?.message || 'Error al obtener los catálogos');
            setHasError(true)
            setIsLoading(false)
        });
        fetch(`${url}catalogs/tax-regime`, {
            method: 'POST',
            body: `{"token": "${props.token}"}`,
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(async response => {
            const data = await response.json();
            if (!response.ok) {
                throw new ApiError(response.status, data);
            }
            return data;
        })
            .then(({ data }) => {
                setTaxRegime(data)
                validateRequest()
                })
        .catch(error => {
            console.error('Error al obtener los catálogos:', error);
            setErrorMessage(error.data?.message || 'Error al obtener los catálogos');
            setHasError(true)
            setIsLoading(false)
        });
    }, [props.token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!clientData.requireInvoice.value) {
            onChangeField({ target: { name: 'isForeign', value: false } })
            onChangeField({ target: { name: 'rfc', value: '' } })
        }
        if (clientData.requireInvoice.value) {
            const firstItem: any = useCfdi[0];
            onChangeField({ target: { name: 'useCfdi', value: firstItem.id } })

        }
    }, [clientData.requireInvoice.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!clientData.isForeign.value) {
            onChangeField({ target: { name: 'tin', value: '' } })
            onChangeField({ target: { name: 'rfc', value: '' } })
        }
        if (clientData.isForeign.value) {
            onChangeField({ target: { name: 'rfc', value: 'XEXX010101000' } })
        }
    }, [clientData.isForeign.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let state = '';
        if (clientData.country.value === '141') {
            state = '1'
        }

        onChangeField({ target: { name: 'state', value: state } })
    }, [clientData.country.value]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSaveClient = () => {
        const { data, hasErrors } = validateData(clientData)

        if (hasErrors) {
            setClientData(data)
            setShowErrors(true)
            return
        }

        const values = getValues(clientData)
        const name = values.requireInvoice ? values.name : values.descripcion_orden;
        setIsLoading(true)
        fetch(`${url}create-order`, {
            method: 'POST',
            body: JSON.stringify({ ...values, name, key: props.token }),
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(async response => {
            if (!response.ok) {
                const data = await response.text();
                throw new ApiError(response.status, data);
            }
            const data = await response.json();
            return data;
        })
            .then(({ data }) => {
                props.setPaymentWays(data)
                navigate(`${context}/gateways?key=${props.token}`);
            })
        .catch(error => {
            setIsLoading(false)
            const errorMsg = error.data || error.message || 'Error al guardar el cliente';
            setToastMessage(errorMsg)
            setToastType('error')
            setShowToast(true)
        });

    }

    const onChangeCif = ({ target }: any) => {
        setCif(target.files[0])
    }

    const clearCif = () => {
        setCif(undefined)
        setCifIsLoaded(false)
        const fileInput = document.getElementById('cif') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    const uploadCif = ({ target }: any) => {
        setIsUploadingCif(true)
        const data = new FormData();
        if (cif) {
            data.append('file', cif, cif.name);
        }
        data.append('key-wg', props.token);
        fetch(`${url}read-cfdi`, {
            method: 'POST',
            body: data,
        }).then(async response => {
            const responseData = await response.json();
            if (!response.ok) {
                throw new ApiError(response.status, responseData);
            }
            return responseData;
        })
            .then(data => {
                const regimen = find(propEq('description_cif', data.regimen))(taxRegime) as { id: string } | undefined;
                const estado = find(propEq('name', data.entidadFederativa))(states) as { id: string } | undefined;
                const clientLocalData = mapPropsToState(clientData, {
                    rfc: data.rfc,
                    name: data.denominacionORazonSocial,
                    street: data.nombreDeLaVialidad,
                    email: data.correoElectronico,
                    state: estado?.id, // Add null check for 'estado'
                    external: data.numeroExterior,
                    interior: data.numeroInterior,
                    zip: data.cp,
                    suburb: data.colonia,
                    municipality: data.municipioODelegacion,
                    taxRegime: regimen?.id,
                })
                setClientData(clientLocalData)
                setCifIsLoaded(true);
                setToastMessage('Archivo CFDI cargado correctamente');
                setToastType('success');
                setShowToast(true);
                setIsUploadingCif(false)
            })
        .catch(error => {
            console.error('Error al leer el CFDI:', error);
            const errorMsg = error.data?.message || error.message || 'Error al leer el CFDI';
            setToastMessage(errorMsg)
            setToastType('error')
            setShowToast(true)
            setIsUploadingCif(false)
        });
    }
    if (hasError) {
        return <ErrorService errorMessage={errorMessage} />
    }

    return (
        <>
            {isLoading && <Loading />}
            <Toast 
                message={toastMessage} 
                type={toastType} 
                isVisible={showToast} 
                onClose={() => setShowToast(false)}
            />
            <div className="w-full min-h-screen">
                <form className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 grid grid-cols-4 gap-2 min-h-screen">

                    <div className="mb-3 col-span-2">
                        <Input
                            {...clientData.descripcion_orden}
                            onChange={onChangeField}
                            value={clientData.descripcion_orden.value}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-4">
                        <label
                            htmlFor="toogleA"
                            className="flex items-center cursor-pointer"
                        >
                            <div className="relative">
                                <input
                                    id="toogleA"
                                    checked={clientData.requireInvoice.value}
                                    onChange={onChangeField}
                                    name="requireInvoice"
                                    type="checkbox" className="hidden" />
                                <div
                                    className="toggle__line w-10 h-4 bg-gray-400 rounded-full shadow-inner"
                                ></div>
                                <div
                                    className="toggle__dot absolute w-6 h-6 bg-white rounded-full shadow inset-y-0 left-0"
                                ></div>
                            </div>
                            <div
                                className="ml-3 text-gray-700 font-medium"
                            >
                                ¿Requiere factura?
                            </div>
                        </label>
                    </div>
                    <div>

                    </div>
                    <div className="mb-3 col-span-4">
                        {clientData.requireInvoice.value && (<label
                            htmlFor="toogleForeign"
                            className="flex items-center cursor-pointer"
                        >
                            <div className="relative">
                                <input
                                    name="isForeign"
                                    onChange={onChangeField}
                                    id="toogleForeign"
                                    type="checkbox"
                                    className="hidden"
                                />
                                <div
                                    className="toggle__line w-10 h-4 bg-gray-400 rounded-full shadow-inner"
                                ></div>
                                <div
                                    className="toggle__dot absolute w-6 h-6 bg-white rounded-full shadow inset-y-0 left-0"
                                ></div>
                            </div>
                            <div
                                className="ml-3 text-gray-700 font-medium"
                            >
                                ¿Es extranjero?
                            </div>
                        </label>)}
                    </div>
                    {(clientData.requireInvoice.value && !clientData.isForeign.value) && (<>
                        <div className="mb-3 col-span-2">
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cif">
                                Constancia de situación fiscal:
                            </label>
                            <div className="flex items-center gap-2">
                                <label 
                                    htmlFor="cif" 
                                    className={`flex-1 border rounded px-3 py-2 cursor-pointer ${cif ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-50`}
                                >
                                    <input
                                        id="cif"
                                        name="cif"
                                        type="file"
                                        onChange={onChangeCif}
                                        accept=".pdf"
                                        className="hidden"
                                    />
                                    <div className="flex items-center justify-between">
                                        <span className={`text-sm ${cif ? 'text-gray-700' : 'text-gray-500'}`}>
                                            {cif ? cif.name : 'Seleccionar archivo PDF'}
                                        </span>
                                        {!cif && (
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                        )}
                                    </div>
                                </label>
                                {cifIsLoaded ? (
                                    <button 
                                        type="button" 
                                        onClick={clearCif}
                                        className="border bg-red-500 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center hover:bg-red-600"
                                        title="Remover archivo"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                ) : (
                                    <button 
                                        type="button" 
                                        onClick={uploadCif} 
                                        disabled={isUploadingCif || !cif}
                                        className={`border bg-gray-100 text-black font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center ${(isUploadingCif || !cif) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {isUploadingCif ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-600 mr-2"></div>
                                                Cargando...
                                            </>
                                        ) : (
                                            'Subir'
                                        )}
                                    </button>
                                )}
                            </div>
                            {cifIsLoaded && <p className="text-green-300 text-sm mt-1">Archivo cargado correctamente</p>}
                        </div>
                        <div className="mb-3 col-span-2">
                            <Input
                                {...clientData.name}
                                onChange={onChangeField}
                                value={clientData.name.value}
                                showErrors={showErrors}
                            />
                        </div>
                        <div className="mb-3 col-span-2">
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                                Uso del Cfdi:
                            </label>
                            <select className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
                                id="useCfdi"
                                name="useCfdi"
                                value={clientData.useCfdi.value}
                                placeholder="Uso del cfdi"
                                onChange={onChangeField}
                            >
                                {useCfdi.map(
                                    (it: { description: string; id: number }) => <option value={it.id}>{it.description}</option>)}
                            </select>
                        </div>
                        <div className="mb-3 col-span-2">
                            <Input
                                {...clientData.rfc}
                                onChange={onChangeField}
                                showErrors={showErrors}
                            />
                        </div>
                    </>)}
                    {(clientData.requireInvoice.value && !clientData.isForeign.value) && <div className="mb-3 col-span-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                            Régimen Fiscal:
                        </label>
                        <select className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
                            id="taxRegime"
                            placeholder="taxRegime"
                            name="taxRegime"
                            onChange={onChangeField}
                            value={clientData.taxRegime.value}
                        >
                            {taxRegime.map(
                                (it: { description: string, id: number }) => <option value={it.id}>{it.description}</option>)}
                        </select>
                    </div>}
                    {clientData.isForeign.value && (<div className="mb-3 col-span-4">
                        <Input
                            {...clientData.tin}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>)}
                    <div className="mb-3 col-span-2">
                        <Input
                            {...clientData.email}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-2">
                        <Input
                            {...clientData.street}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-1">
                        <Input
                            {...clientData.external}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-1">
                        <Input
                            {...clientData.interior}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-2">
                        <Input
                            {...clientData.zip}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-2">
                        <Input
                            {...clientData.suburb}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-2">
                        <Input
                            {...clientData.municipality}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="mb-3 col-span-2">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                            Estado:
                        </label>
                        {(clientData.country.value !== '141') && <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline" id="password" type="text"
                            placeholder="Estado"
                            name="state"
                            onChange={onChangeField}
                            value={clientData.state.value}
                            disabled={true}
                        />}
                        {(clientData.country.value === '141') && <select className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
                            id="state"
                            placeholder="Estado"
                            name="state"
                            onChange={onChangeField}
                            value={clientData.state.value}
                        >
                            {states.map(
                                (it: { name: string, id: number }) => <option value={it.id}>{it.name}</option>)}
                        </select>}
                    </div>
                    <div className="mb-3 col-span-2">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                            País:
                        </label>
                        <select className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
                            id="country"
                            placeholder="Pais"
                            name="country"
                            onChange={onChangeField}
                            value={clientData.country.value}
                        >
                            {countries.map(
                                (it: { country: string, id: number }) => <option value={it.id}>{it.country}</option>)}
                        </select>
                    </div>
                    <div className="mb-7 col-span-2">
                        <Input
                            {...clientData.phone}
                            onChange={onChangeField}
                            showErrors={showErrors}
                        />
                    </div>
                    <div className="flex items-center justify-between mb-4 col-span-4">
                        <button type="button" onClick={onSaveClient} className="w-full bg-red-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
                            Continuar
                        </button>
                    </div>
                </form>
            </div>
        </>
    )
}
